import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  DeleteForever as DeleteIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Notifications as NotificationsIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import apiClient from '../services/api';

interface UserData {
  id: number;
  telegram_id: number;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  email: string | null;
  agreements_accepted: boolean;
  created_at: string;
}

interface Statistics {
  events_created: number;
  events_participated: number;
  applications_submitted: number;
  notifications_received: number;
}

const MyDataPage: React.FC = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [participations, setParticipations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Статистика организатора
  const [organizerStats, setOrganizerStats] = useState<any>(null);
  const [organizerStatsLoading, setOrganizerStatsLoading] = useState(false);
  const [statsPeriod, setStatsPeriod] = useState(30);

  // Диалоги
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Редактирование профиля
  const [editMode, setEditMode] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editPhoneNumber, setEditPhoneNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/personal-data/my');
      setUserData(response.data.user);
      setStatistics(response.data.statistics);
      setEvents(response.data.events);
      setParticipations(response.data.participations);
    } catch (err: any) {
      console.error('Ошибка загрузки данных:', err);
      setError('Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  };
  
  // Загрузка статистики организатора
  const loadOrganizerStats = async (days: number = 30) => {
    try {
      setOrganizerStatsLoading(true);
      setStatsPeriod(days);
      const response = await apiClient.get(`/api/organizers/${userData?.id}/stats?days=${days}`);
      setOrganizerStats(response.data);
    } catch (err: any) {
      console.error('Ошибка загрузки статистики:', err);
      // Не показываем ошибку, просто оставляем null
    } finally {
      setOrganizerStatsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Синхронизация данных редактирования при загрузке
  useEffect(() => {
    if (userData) {
      setEditFirstName(userData.first_name || '');
      setEditLastName(userData.last_name || '');
      setEditPhoneNumber(userData.phone_number || '');
    }
  }, [userData]);

  // Сохранение изменений профиля
  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setSaveMessage(null);
      const response = await apiClient.put('/api/personal-data/profile', {
        first_name: editFirstName || null,
        last_name: editLastName || null,
        phone_number: editPhoneNumber || null,
      });
      setUserData(response.data.user);

      // Обновляем данные в authStore, чтобы ProfilePage тоже увидел изменения
      const authStore = useAuthStore.getState();
      if (authStore.user) {
        useAuthStore.setState({
          user: {
            ...authStore.user,
            first_name: response.data.user.first_name,
            last_name: response.data.user.last_name,
            phone_number: response.data.user.phone_number,
          },
        });
      }

      setEditMode(false);
      setSaveMessage('✅ Изменения сохранены');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err: any) {
      console.error('Ошибка сохранения:', err);
      setSaveMessage('❌ Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  };

  // Отмена редактирования
  const handleCancelEdit = () => {
    setEditMode(false);
    if (userData) {
      setEditFirstName(userData.first_name || '');
      setEditLastName(userData.last_name || '');
      setEditPhoneNumber(userData.phone_number || '');
    }
  };
  
  // Автоматическая загрузка статистики организатора после загрузки данных
  useEffect(() => {
    if (userData && statistics && statistics.events_created > 0) {
      loadOrganizerStats(30);
    }
  }, [userData]);

  const handleDeleteAccount = async () => {
    if (deleteConfirmText.toLowerCase() !== 'удалить аккаунт') {
      alert('Введите: "удалить аккаунт"');
      return;
    }

    try {
      await apiClient.delete('/api/personal-data/account', {
        data: { confirm_text: deleteConfirmText }
      });
      setDeleteDialogOpen(false);
      
      // Очищаем всё состояние аутентификации
      useAuthStore.getState().logout();
      
      navigate('/login');
      alert('Аккаунт удалён');
    } catch (err: any) {
      console.error('Ошибка удаления аккаунта:', err);
      alert(err.response?.data?.detail || 'Ошибка при удалении аккаунта');
    }
  };

  if (loading) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/profile')} sx={{ mb: 2 }}>
          Назад
        </Button>
        <Typography>Загрузка данных...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/profile')} sx={{ mb: 2 }}>
          Назад
        </Button>
        <Paper sx={{ p: 3, bgcolor: 'error.light', color: 'error.contrastText' }}>
          <Typography variant="body1" fontWeight="bold">
            ❌ Ошибка
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Заголовок */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/profile')}>
            Назад к профилю
          </Button>
          <Typography variant="h4" component="h1" fontWeight="bold">
            📊 Мои персональные данные
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Удалить аккаунт
          </Button>
        </Box>
      </Box>

      {/* Основная информация */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          👤 Личная информация
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={2}>
          {/* Имя */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Имя</Typography>
            {editMode ? (
              <TextField
                fullWidth
                size="small"
                value={editFirstName}
                onChange={(e) => setEditFirstName(e.target.value)}
                placeholder="Ваше имя"
              />
            ) : (
              <Typography variant="body1">{userData?.first_name || '—'}</Typography>
            )}
          </Grid>
          {/* Username */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Username</Typography>
            <Typography variant="body1">{userData?.username || '—'}</Typography>
          </Grid>
          {/* Фамилия */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Фамилия</Typography>
            {editMode ? (
              <TextField
                fullWidth
                size="small"
                value={editLastName}
                onChange={(e) => setEditLastName(e.target.value)}
                placeholder="Ваша фамилия"
              />
            ) : (
              <Typography variant="body1">{userData?.last_name || '—'}</Typography>
            )}
          </Grid>
          {/* Телефон */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Телефон</Typography>
            {editMode ? (
              <TextField
                fullWidth
                size="small"
                value={editPhoneNumber}
                onChange={(e) => setEditPhoneNumber(e.target.value)}
                placeholder="+7 (999) 123-45-67"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            ) : (
              <Typography variant="body1">{userData?.phone_number || '—'}</Typography>
            )}
          </Grid>
          {/* Email */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Email</Typography>
            <Typography variant="body1">{userData?.email || '—'}</Typography>
          </Grid>
          {/* Согласие на обработку ПДн */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Согласие на обработку ПДн</Typography>
            <Chip
              label="✅ Дано"
              color="success"
              size="small"
            />
          </Grid>
          {/* Дата регистрации */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Дата регистрации</Typography>
            <Typography variant="body1">
              {userData?.created_at ? new Date(userData.created_at).toLocaleDateString('ru-RU') : '—'}
            </Typography>
          </Grid>
        </Grid>

        {/* Кнопки редактирования */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {!editMode ? (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setEditMode(true)}
              size="small"
            >
              Редактировать
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveProfile}
                disabled={saving}
                size="small"
              >
                {saving ? 'Сохранение...' : 'Сохранить'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancelEdit}
                disabled={saving}
                size="small"
              >
                Отмена
              </Button>
            </>
          )}
          {saveMessage && (
            <Typography variant="body2" color={saveMessage.startsWith('✅') ? 'success.main' : 'error.main'} fontWeight="bold">
              {saveMessage}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventIcon color="primary" />
                <Typography variant="h6">{statistics?.events_created}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Создано мероприятий
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="success" />
                <Typography variant="h6">{statistics?.events_participated}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Участий в мероприятиях
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsIcon color="info" />
                <Typography variant="h6">{statistics?.notifications_received}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Уведомлений получено
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Статистика организатора */}
      {statistics && statistics.events_created > 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              📈 Статистика организатора
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant={statsPeriod === 7 ? 'contained' : 'outlined'}
                onClick={() => loadOrganizerStats(7)}
              >
                7 дней
              </Button>
              <Button
                size="small"
                variant={statsPeriod === 30 ? 'contained' : 'outlined'}
                onClick={() => loadOrganizerStats(30)}
              >
                30 дней
              </Button>
              <Button
                size="small"
                variant={statsPeriod === 90 ? 'contained' : 'outlined'}
                onClick={() => loadOrganizerStats(90)}
              >
                90 дней
              </Button>
            </Box>
          </Box>

          {organizerStatsLoading ? (
            <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Загрузка статистики...
              </Typography>
            </Paper>
          ) : organizerStats ? (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light' }}>
                    <Typography variant="h4" fontWeight="bold" color="primary.contrastText">
                      {organizerStats.summary.total_views}
                    </Typography>
                    <Typography variant="body2" color="primary.contrastText">
                      Просмотров
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                    <Typography variant="h4" fontWeight="bold" color="success.contrastText">
                      {organizerStats.summary.registrations}
                    </Typography>
                    <Typography variant="body2" color="success.contrastText">
                      Регистраций
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
                    <Typography variant="h4" fontWeight="bold" color="info.contrastText">
                      {organizerStats.summary.event_joins}
                    </Typography>
                    <Typography variant="body2" color="info.contrastText">
                      Записей на мероприятия
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
                    <Typography variant="h4" fontWeight="bold" color="warning.contrastText">
                      {organizerStats.summary.conversion_rate}%
                    </Typography>
                    <Typography variant="body2" color="warning.contrastText">
                      Конверсия
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    По источникам:
                  </Typography>
                  {Object.entries(organizerStats.sources || {}).map(([source, count]) => (
                    <Box key={source} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="body2" textTransform="capitalize">
                        {source === 'qr' ? '📱 QR-код' :
                         source === 'link' ? '🔗 Прямая ссылка' :
                         source === 'social' ? '📱 Соцсети' :
                         source === 'email' ? '📧 Email' :
                         source === 'messenger' ? '💬 Мессенджеры' :
                         source === 'direct' ? '🌐 Прямой заход' : source}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {count as number}
                      </Typography>
                    </Box>
                  ))}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Топ мероприятий:
                  </Typography>
                  {(organizerStats.top_events || []).slice(0, 5).map((event: any, index: number) => (
                    <Box key={event.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: '70%' }}>
                        {index + 1}. {event.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography variant="body2" color="primary">
                          👁️ {event.views}
                        </Typography>
                        <Typography variant="body2" color="success">
                          ✅ {event.joins || 0}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </Paper>
          ) : (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                📌 Статистика организатора доступна для пользователей, создавших хотя бы одно мероприятие.
                Если вы уже создали мероприятие, нажмите "Загрузить статистику".
              </Typography>
              <Button
                variant="contained"
                onClick={() => loadOrganizerStats(30)}
                sx={{ mt: 2 }}
              >
                Загрузить статистику
              </Button>
            </Paper>
          )}
        </>
      )}

      {/* Созданные мероприятия */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          🎯 Созданные мероприятия
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {events.length === 0 ? (
          <Typography color="text.secondary">Нет созданных мероприятий</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Название</TableCell>
                  <TableCell>Дата</TableCell>
                  <TableCell>Статус</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.slice(0, 5).map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>{event.title}</TableCell>
                    <TableCell>{new Date(event.start_datetime).toLocaleDateString('ru-RU')}</TableCell>
                    <TableCell>
                      <Chip
                        label={event.status === 'open' ? 'Активно' : event.status === 'closed' ? 'Завершено' : 'Отменено'}
                        size="small"
                        color={event.status === 'open' ? 'success' : event.status === 'closed' ? 'warning' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Участие */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          📅 Участие в мероприятиях
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {participations.length === 0 ? (
          <Typography color="text.secondary">Нет участия в мероприятиях</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Мероприятие</TableCell>
                  <TableCell>Дата</TableCell>
                  <TableCell>Дата записи</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {participations.slice(0, 5).map((part) => (
                  <TableRow key={part.event_id}>
                    <TableCell>{part.title}</TableCell>
                    <TableCell>{new Date(part.start_datetime).toLocaleDateString('ru-RU')}</TableCell>
                    <TableCell>{new Date(part.joined_at).toLocaleDateString('ru-RU')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Диалог удаления аккаунта */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>❌ Удаление аккаунта</DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          <Typography paragraph>
            Вы собираетесь удалить свой аккаунт. Это действие необратимо!
          </Typography>
          <Typography paragraph>
            Все ваши персональные данные будут удалены, за исключением данных, 
            которые должны храниться в соответствии с законодательством РФ.
          </Typography>
          <TextField
            fullWidth
            label="Введите для подтверждения: удалить аккаунт"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleDeleteAccount} color="error" variant="contained">
            Удалить аккаунт
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyDataPage;
